import { useState, useEffect } from 'react';
import { useReduxState } from 'hooks';
import { RichTextValue } from 'components/common';
import { defaultsByBlockType } from 'shared-helpers/consts';
import { EditorBlockTranslationReference } from 'components/editor';
import { ListEditor } from 'components/editor/inputs';
import { EditorBlockListControls } from '.';

import type { BlockList } from 'shared-types';
import type {
  BlockProps,
  EditorStateProps
} from 'components/editor/blocks/EditorBlock';
import type { ListEditorHandle } from 'components/editor/inputs/ListEditor';

type StateProps = Pick<
  EditorStateProps,
  'editorLanguageActive' | 'editorLanguageReference'
>;

const EditorBlockList = ({
  blockId,
  data,
  isAIPreview,
  isPreview,
  languageVersion,
  updateData,
  ...stateProps
}: BlockProps<BlockList> & StateProps) => {
  const { editorLanguageActive, editorLanguageReference } = stateProps;

  const {
    pageState: { course }
  } = useReduxState();

  const [handler, setHandler] = useState<ListEditorHandle | null>(null);
  const [isOrderedList, setIsOrderedList] = useState<boolean>(false);
  const [isListStartVisible, setIsListStartVisible] = useState<boolean>(false);
  const [listStartValue, setListStartValue] = useState<number>(1);

  useEffect(() => {
    if (!handler) return;

    const { type, start } = handler?.getTypeAndStart() || {};
    if (type === 'ol_list' && !isOrderedList) {
      setIsOrderedList(true);
      setListStartValue(start);
    }
  }, []);

  // match starting list tag in target html with reference html start tag
  const getUpdatedListHtml = (referenceHtml: string, targetHtml: string) => {
    const refOpenTag = referenceHtml.match(/^<[^>]+>/)[0];
    const updatedHtml = targetHtml.replace(/^<[^>]+>/, refOpenTag);
    return updatedHtml;
  };

  const handleChange = (html: string) => {
    const locales = Object.entries(data.locales).reduce((acc, [key, value]) => {
      if (key === languageVersion) {
        // update current language version data
        acc[key] = {
          ...value,
          translatable: { ...value.translatable, listHtml: html }
        };
      } else if (!editorLanguageActive) {
        // when using language versions, if the source list html type changes,
        // we want to sync all locales so the type matches
        acc[key] = {
          ...value,
          translatable: {
            ...value.translatable,
            listHtml: getUpdatedListHtml(html, value.translatable.listHtml)
          }
        };
      } else {
        // keep data unchanged
        acc[key] = { ...value };
      }
      return acc;
    }, {});

    updateData({ ...data, locales });
  };

  const handleIndent = () => handler?.increaseIndentation();

  const handleOutdent = () => handler?.decreaseIndentation();

  const handleListStartClick = () => setIsListStartVisible(!isListStartVisible);

  const handleListStartChange = (value: number) => {
    handler?.listStartChange(value);
    setListStartValue(value);
  };

  const handleListTypeChange = (type: 'ol_list' | 'ul_list') => {
    setIsOrderedList(type === 'ol_list');
    setIsListStartVisible(false);
  };

  const handleOlClick = () => {
    handler?.toggleType('ol_list');
    setIsOrderedList(true);
    setIsListStartVisible(false);
  };

  const handleUlClick = () => {
    handler?.toggleType('ul_list');
    setIsOrderedList(false);
    setIsListStartVisible(false);
  };

  // capture the default data for a non-translated block
  // based on the type of list element being used in the course language
  const referenceData =
    data?.locales?.[course.language]?.translatable?.listHtml;

  let defaultListHtml = referenceData?.includes('<ol')
    ? defaultsByBlockType.list.defaultOrderedListHtml
    : defaultsByBlockType.list.defaultUnorderedListHtml;
  if (referenceData) {
    defaultListHtml = referenceData.replace(
      /(<p\b[^>]*>)[\s\S]*?(<\/p>)/gi,
      '$1$2'
    );
  }

  const editingLanguageVersion = Boolean(editorLanguageActive);

  return (
    <>
      <div className="list">
        <EditorBlockTranslationReference
          referenceHtml={
            data.locales[editorLanguageReference]?.translatable?.listHtml || ''
          }
          translationHtml={data.locales[languageVersion].translatable.listHtml}
        >
          {isAIPreview || isPreview ? (
            <RichTextValue
              html={data.locales[languageVersion].translatable.listHtml}
            />
          ) : (
            <ListEditor
              content={
                data.locales[languageVersion].translatable.listHtml ||
                defaultListHtml
              }
              disabled={editingLanguageVersion}
              getHandler={setHandler}
              onChange={handleChange}
              setSelectionListType={handleListTypeChange}
            />
          )}
        </EditorBlockTranslationReference>

        {!isAIPreview && !isPreview && !editingLanguageVersion ? (
          <EditorBlockListControls
            blockId={blockId}
            data={data}
            isListStartVisible={isListStartVisible}
            isOrderedList={isOrderedList}
            languageVersion={languageVersion}
            listStartValue={listStartValue}
            onHighlightList={handler?.highlightList}
            onListStartChange={handleListStartChange}
            onListStartClick={handleListStartClick}
            onOlClick={handleOlClick}
            onUlClick={handleUlClick}
            onIndent={handleIndent}
            onOutdent={handleOutdent}
          />
        ) : null}

        <style jsx>{`
          .list :global(li > p) {
            margin: 0;
          }
          .list :global(ul),
          .list :global(ol) {
            transition: background-color 0.2s;
          }
        `}</style>
      </div>
    </>
  );
};

export default EditorBlockList;
